// src/components/SignatureCapture.js
import React, { useRef, useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, storage } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

const SignatureCapture = ({ shipmentId, type, onSave, currentUser, isDriver = false, driverId = null, location = null, requirePhotos = false, currentPhotos = [] }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toLocaleDateString());
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user: authUser } = useAuth();

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Clear canvas to white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  // Load shipment data and pre-fill names
  useEffect(() => {
    const loadShipmentData = async () => {
      if (!shipmentId) return;
      
      try {
        const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
        if (shipmentDoc.exists()) {
          const shipmentData = shipmentDoc.data();
          setShipment(shipmentData);
          
          // Pre-fill names based on context
          if (isDriver && type === 'driver') {
            // For driver signature via driver link
            setName(shipmentData.assignedDriver?.driverName || 'Driver');
          } else if (isDriver && type === 'customer') {
            // For customer signature via driver link
            setName(shipmentData.userName || 'Customer');
          } else if (currentUser) {
            // For admin signature capture
            setName(currentUser.displayName || 'Admin User');
          }
        }
      } catch (error) {
        console.error('Error loading shipment data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadShipmentData();
  }, [shipmentId, type, isDriver, currentUser]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Get coordinates (handle both mouse and touch events)
    let x, y;
    if (e.type.includes('touch')) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Get coordinates (handle both mouse and touch events)
    let x, y;
    if (e.type.includes('touch')) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    setIsDrawing(false);
    // Auto-capture signature when drawing stops
    setTimeout(captureSignature, 100);
  };

  const saveSignature = async () => {
    if (!signatureData) {
      alert('Please provide a signature first');
      return;
    }

    if (!name.trim()) {
      alert('Please enter your name');
      return;
    }

    // Check if photos are required but not provided
    if (requirePhotos && currentPhotos.length === 0) {
      alert('Please upload at least one photo before saving the signature');
      return;
    }

    setUploading(true);

    try {
      console.log('Saving signature as data URL...');
      
      // Save signature as data URL directly (not as file upload)
      const signatureDataUrl = signatureData;

      let photoDownloadURL = null;
      if (photo) {
        // Upload photo separately
        const photoRef = ref(storage, `photos/${shipmentId}/${type}-photo-${Date.now()}.jpg`);
        await uploadBytes(photoRef, photo);
        photoDownloadURL = await getDownloadURL(photoRef);
      }

      // Prepare signature data
      const signaturePayload = {
        signature: signatureDataUrl, // Save as data URL for PDF embedding
        name: name,
        date: date,
        photo: photoDownloadURL, // Photo is still saved as URL
        signedAt: serverTimestamp(),
        signatureType: 'data_url' // Mark as data URL for reference
      };

      // Add signedBy information based on context
      if (isDriver && driverId) {
        // Driver signing via driver link
        signaturePayload.signedBy = driverId;
        signaturePayload.signedByDriver = true;
        signaturePayload.driverName = shipment?.assignedDriver?.driverName || name;
        // Add location context for driver signatures
        if (location) {
          signaturePayload.location = location;
        }
      } else if (currentUser) {
        // Admin signing
        signaturePayload.signedBy = currentUser.uid;
        signaturePayload.signedByAdmin = true;
        signaturePayload.adminName = currentUser.displayName || name;
      } else if (authUser) {
        // Customer signing (if implemented)
        signaturePayload.signedBy = authUser.uid;
        signaturePayload.signedByCustomer = true;
      }

      // Update Firestore with signature data
      await updateDoc(doc(db, 'shipments', shipmentId), {
        [`signatures.${type}`]: signaturePayload,
        updatedAt: serverTimestamp()
      });

      console.log('Signature saved successfully as data URL');
      console.log('Signature type:', type);
      console.log('Signed by:', signaturePayload.signedBy);

      // If this is a driver signature and both signatures are complete, auto-generate BOL
      if (isDriver && type === 'delivery') {
        const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
        if (shipmentDoc.exists()) {
          const updatedShipment = shipmentDoc.data();
          const hasPickupSignature = updatedShipment.signatures?.pickup?.signature;
          const hasDeliverySignature = updatedShipment.signatures?.delivery?.signature;
          
          if (hasPickupSignature && hasDeliverySignature) {
            console.log('Both signatures complete - shipment ready for BOL generation');
            // You could auto-trigger BOL generation here if needed
          }
        }
      }

      onSave && onSave(signaturePayload);
    } catch (error) {
      console.error('Error saving signature:', error);
      alert('Error saving signature. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Please select an image smaller than 5MB');
        return;
      }
      
      setPhoto(file);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear to white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Reset stroke style
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    setSignatureData(null);
  };

  const captureSignature = () => {
    const canvas = canvasRef.current;
    
    // Check if there's actually a signature (not blank canvas)
    const blankCanvas = document.createElement('canvas');
    blankCanvas.width = canvas.width;
    blankCanvas.height = canvas.height;
    const blankCtx = blankCanvas.getContext('2d');
    blankCtx.fillStyle = '#FFFFFF';
    blankCtx.fillRect(0, 0, blankCanvas.width, blankCanvas.height);
    
    if (canvas.toDataURL() === blankCanvas.toDataURL()) {
      // Canvas is blank, don't capture
      console.log('Canvas is blank - no signature to capture');
      return;
    }
    
    const signatureUrl = canvas.toDataURL('image/png');
    setSignatureData(signatureUrl);
    console.log('Signature captured as data URL');
  };

  // Get appropriate labels based on context
  const getLabels = () => {
    if (isDriver) {
      return {
        title: type === 'driver' ? 'Driver Signature' : 'Customer Signature',
        nameLabel: type === 'driver' ? 'Driver Name' : 'Customer Name',
        namePlaceholder: type === 'driver' ? 'Enter driver name' : 'Enter customer name',
        buttonText: `Save Signature`
      };
    } else {
      return {
        title: type === 'driver' ? 'Driver Signature & Photo' : 'Customer Signature & Photo',
        nameLabel: type === 'driver' ? 'Driver Name' : 'Customer Name',
        namePlaceholder: type === 'driver' ? 'Enter driver name' : 'Enter customer name',
        photoLabel: 'Upload Photo Evidence (Optional)',

        buttonText: `Save Signature`
      };
    }
  };

  const labels = getLabels();

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="loading-spinner"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="signature-capture p-6 border rounded-lg bg-white">
      <h3 className="text-lg font-semibold mb-4">
        {labels.title}
      </h3>
      
      {/* Shipment Info for Driver Context */}
      {isDriver && shipment && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Shipment Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div>
              <strong>ID:</strong> {shipment.displayId || shipment.id}
            </div>
            <div>
              <strong>Item:</strong> {shipment.itemDescription}
            </div>
            <div className="md:col-span-2">
              <strong>Pickup:</strong> {shipment.pickupAddress}
            </div>
            <div className="md:col-span-2">
              <strong>Delivery:</strong> {shipment.dropoffAddress}
            </div>
          </div>
        </div>
      )}
      
      {/* Name and Date Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {labels.nameLabel} *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={labels.namePlaceholder}
            readOnly={isDriver && type === 'driver'} // Driver name is pre-filled and read-only
          />
          {isDriver && type === 'driver' && (
            <p className="text-xs text-gray-500 mt-1">Pre-filled from assignment</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date *
          </label>
          <input
            type="text"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="MM/DD/YYYY"
          />
        </div>
      </div>

      {/* Signature Canvas */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Draw Your Signature:
        </label>
        <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
          <canvas
            ref={canvasRef}
            width={500}
            height={200}
            className="w-full h-48 border border-gray-200 rounded cursor-crosshair bg-white touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={endDrawing}
            onMouseLeave={endDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={endDrawing}
            style={{ touchAction: 'none' }}
          />
        </div>
        <div className="mt-2 space-x-2">
          <button
            onClick={captureSignature}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Capture Signature
          </button>
          <button
            onClick={clearSignature}
            className="px-4 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
          >
            Clear Signature
          </button>
        </div>
        {signatureData && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-700 font-semibold">✓ Signature captured successfully!</p>
            <p className="text-xs text-green-600">Signature will appear on the BOL document</p>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Draw your signature in the box above. Signature will be automatically captured when you finish drawing.
        </p>
      </div>

      {/* Photo Upload */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {labels.photoLabel}:
        </label>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoCapture}
          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        {photo && (
          <p className="text-sm text-green-600 mt-1">✓ Photo selected</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {labels.photoDescription}
        </p>
        {requirePhotos && currentPhotos.length === 0 && (
          <p className="text-sm text-red-600 mt-1">⚠️ Photos are required for this step</p>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={saveSignature}
        disabled={!signatureData || !name.trim() || uploading || (requirePhotos && currentPhotos.length === 0)}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
      >
        {uploading ? 'Saving...' : labels.buttonText}
      </button>

      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
        <p className="text-xs text-blue-700">
          <strong>Note:</strong> Signatures are saved as high-quality images and will appear directly on the Bill of Lading document.
          {isDriver ? (
            type === 'driver' 
              ? ' Driver signature is for pickup verification.'
              : ' Customer signature is for delivery confirmation.'
          ) : (
            type === 'driver' 
              ? ' Driver signature is for pickup verification.'
              : ' Customer signature is for delivery confirmation.'
          )}
        </p>
      </div>
    </div>
  );
};

export default SignatureCapture;